import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import moment from 'moment';

interface PmContractState {
  id: string;
  accountManagerId: string;
  accountSaleId: string;
  companyId: string;
  pmOfferId: any;
  number: string;
  date: string;
  dateStart: string;
  dateEnd: string;
  enumPmOfferContractType: any;
  enumPmOfferContractTypeId: any;
  enumPmContractStatus: any;
  enumPmContractStatusId: any;
  metadata: any;
  remarks: any;
  haveFile: any;
  file: any;
  registrationNumber: any;
  accountManager: any;
  accountSale: any;
  company: any;
  pmContractIntervals: any[];
  pmContractWorkPlaceItems: any[];
  pmContractLaborExtras: any[];
  pmContractCommentsTab: any[];
  location: any[];
  equipmentTab: {
    [key: string]: any;
  };
  laborTab: {
    [key: string]: any;
  };
  props: {
    [key: string]: any;
  };
}

const currentDate = moment().format('YYYY-MM-DD');
const currentDateEnd = moment().add(1, 'year').format('YYYY-MM-DD');

const initialState: PmContractState = {
  id: '',
  accountManagerId: '',
  accountSaleId: '',
  companyId: '',
  pmOfferId: '',
  enumPmOfferContractTypeId: 0,
  enumPmContractStatusId: 0,
  number: '',
  date: currentDate,
  dateStart: currentDate,
  dateEnd: currentDateEnd,
  remarks: '',
  registrationNumber: '',
  haveFile: false,
  file: '',
  metadata: null,
  accountManager: null,
  accountSale: null,
  company: null,
  enumPmOfferContractType: null,
  enumPmContractStatus: null,
  pmContractIntervals: [],
  pmContractWorkPlaceItems: [],
  pmContractLaborExtras: [],
  pmContractCommentsTab: [],
  location: [],
  equipmentTab: { workPlaceId: null },
  laborTab: { laborPriceOnHour: 300 },
  props: { searchTerm: '', skip: 1, take: 10, totalItems: 0 },
};

const pmContractSlice = createSlice({
  name: 'pmContract',
  initialState,
  reducers: {
    setPmContract: (state, action: PayloadAction<Partial<PmContractState>>) => {
      return { ...state, ...action.payload };
    },
    updateField: (state: any, action: PayloadAction<{ field: any; value: any }>) => {
      const { field, value } = action.payload;
      state[field] = value;
    },
    updateEquipmentTab: (state, action: PayloadAction<{ field: string; value: any }>) => {
      const { field, value } = action.payload;
      // Dynamically update equipmentTab with the specified field and value
      state.equipmentTab = {
        ...state.equipmentTab,
        [field]: value,
      };
    },
    updateLaborTab: (state, action: PayloadAction<{ field: string; value: any }>) => {
      const { field, value } = action.payload;
      // Dynamically update equipmentTab with the specified field and value
      state.laborTab = {
        ...state.laborTab,
        [field]: value,
      };
    },
    addPmContractWorkPlaceItem: (state, action) => {
      state.pmContractWorkPlaceItems.push(action.payload);
    },
    updatePmContractWorkPlaceItem: (state, action: PayloadAction<{ index: number; updates: any }>) => {
      const { index, updates } = action.payload;
      if (state.pmContractWorkPlaceItems[index]) {
        state.pmContractWorkPlaceItems[index] = {
          ...state.pmContractWorkPlaceItems[index],
          ...updates,
        };
      }
    },
    removePmContractWorkPlaceItem: (state, action: PayloadAction<number>) => {
      const index = action.payload;
      if (index >= 0 && index < state.pmContractWorkPlaceItems.length) {
        state.pmContractWorkPlaceItems.splice(index, 1);
      }
    },
    // **Add interval actions**
    addPmContractInterval: (state, action: PayloadAction<any>) => {
      state.pmContractIntervals.push(action.payload);
    },
    updatePmContractInterval: (state, action: PayloadAction<{ index: number; updates: Partial<any> }>) => {
      const { index, updates } = action.payload;
      if (state.pmContractIntervals[index]) {
        state.pmContractIntervals[index] = {
          ...state.pmContractIntervals[index],
          ...updates,
        };
      }
    },
    removePmContractInterval: (state, action: PayloadAction<number>) => {
      const index = action.payload;
      if (index >= 0 && index < state.pmContractIntervals.length) {
        state.pmContractIntervals.splice(index, 1);
      }
    },
    //
    addPmContractLaborExtra: (state, action) => {
      state.pmContractLaborExtras.push(action.payload);
    },
    addPmContractComment: (state, action: PayloadAction<any>) => {
      state.pmContractCommentsTab.push(action.payload);
    },
    updatePmContractComment: (
      state,
      action: PayloadAction<{
        departmentName: string;
        updates: Partial<any>;
      }>
    ) => {
      const { departmentName, updates } = action.payload;
      const index = state.pmContractCommentsTab.findIndex((comment) => comment.departmentName === departmentName);

      if (index !== -1) {
        state.pmContractCommentsTab[index] = {
          ...state.pmContractCommentsTab[index],
          ...updates,
        };
      } else {
        state.pmContractCommentsTab.push({
          departmentName,
          ...updates,
        });
      }
    },
    // Remove a file from a comment
    removePmContractComment: (
      state,
      action: PayloadAction<{
        departmentName: string;
        fileUid: string;
      }>
    ) => {
      const { departmentName, fileUid } = action.payload;
      const commentIndex = state.pmContractCommentsTab.findIndex(
        (comment) => comment.departmentName === departmentName
      );

      if (commentIndex !== -1) {
        const comment = state.pmContractCommentsTab[commentIndex];

        const updatedFiles = (comment.pmContractCommentFiles || []).filter(
          (file: any) => file.uid !== fileUid && file.id !== fileUid
        );

        state.pmContractCommentsTab[commentIndex] = {
          ...comment,
          pmContractCommentFiles: updatedFiles,
        };
      }
    },
    updateProps: (state, action: PayloadAction<{ field: string; value: any }>) => {
      const { field, value } = action.payload;
      state.props = {
        ...state.props,
        [field]: value,
      };
    },
  },
});

export const {
  setPmContract,
  updateField,
  updateEquipmentTab, // Export the new action
  updateLaborTab,
  addPmContractWorkPlaceItem,
  updatePmContractWorkPlaceItem,
  removePmContractWorkPlaceItem,
  addPmContractInterval,
  updatePmContractInterval,
  removePmContractInterval,
  addPmContractLaborExtra,
  addPmContractComment,
  updatePmContractComment,
  removePmContractComment,
  updateProps,
} = pmContractSlice.actions;

export default pmContractSlice.reducer;
